import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "@phosphor-icons/react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api } from "@/lib/api";

/**
 * Privacy Policy pubblica.
 * Contenuto gestito da admin in `/admin/public-content` → tab "Privacy Policy".
 * Se non c'è ancora un documento pubblicato, viene mostrato un fallback statico
 * (aggiornato Iter208.7 — sezioni Google OAuth: Condivisione dati + Protezione dati sensibili).
 */
const STATIC_FALLBACK_BODY = `# Informativa Privacy e Termini di Utilizzo

Ultimo aggiornamento: ${new Date().toLocaleDateString("it-IT")}

## 1. Titolare del trattamento
Housetag.it — con sede legale in Italia. Contatti: privacy@housetag.it

## 2. Dati trattati
Trattiamo dati identificativi (nome, cognome, ragione sociale), di contatto (email, PEC, telefono), fiscali (P.IVA), professionali (categoria, ruolo) e i dati generati dall'utilizzo della piattaforma (immobili, pratiche, documenti, messaggi). Se accedi con il tuo account Google (Google OAuth 2.0), riceviamo esclusivamente le informazioni necessarie all'autenticazione (nome, cognome, indirizzo email, immagine profilo) e, solo previo tuo consenso esplicito, l'accesso in lettura/scrittura al tuo Google Calendar per sincronizzare gli appuntamenti creati in HouseTag.

## 3. Finalità
- Erogazione dei servizi Housetag (gestionale, marketplace, workflow operativo)
- Adempimenti contrattuali, fiscali e di legge
- Sicurezza, prevenzione frodi e audit
- Comunicazioni di servizio (approvazioni, notifiche, promemoria)

## 4. Base giuridica
Esecuzione del contratto (art. 6.1.b GDPR), obblighi di legge (art. 6.1.c GDPR), legittimo interesse (art. 6.1.f GDPR) e consenso esplicito ove necessario.

## 5. Conservazione
I dati sono conservati per la durata del rapporto contrattuale e per i 10 anni successivi ai fini fiscali. Log tecnici conservati per 6 mesi.

## 6. Comunicazione e diffusione
I dati possono essere comunicati a operatori interni Housetag per la lavorazione delle pratiche, a fornitori tecnologici (hosting cloud EU), a professionisti del settore per i servizi richiesti, alle autorità competenti su richiesta di legge.

## 7. Condivisione dei dati con terze parti
I dati dell'account Google (nome, indirizzo email, immagine profilo, dati di Google Calendar) e, più in generale, tutti i dati personali degli utenti trattati da HouseTag:

- **NON** vengono condivisi con terze parti per finalità di marketing, profilazione o pubblicità
- **NON** vengono venduti, ceduti né affittati ad alcuna terza parte, partner commerciale, inserzionista o data broker
- **NON** vengono utilizzati per addestrare modelli di intelligenza artificiale generici né divulgati al di fuori dei sistemi HouseTag

I dati raccolti tramite l'accesso al tuo account Google e al tuo Google Calendar sono utilizzati **esclusivamente per il funzionamento interno della piattaforma HouseTag** (autenticazione, personalizzazione dell'esperienza utente, creazione e sincronizzazione di appuntamenti e promemoria collegati alle tue pratiche). Nessuna informazione derivata dal tuo account Google viene mai trasferita a terzi al di fuori dei fornitori tecnologici strettamente necessari all'erogazione del servizio (hosting cloud in UE, invio email transazionali), i quali agiscono in qualità di Responsabili del trattamento ex art. 28 GDPR e sono vincolati contrattualmente al rispetto delle stesse tutele descritte in questa informativa.

Puoi revocare in ogni momento l'autorizzazione all'accesso al tuo account Google dalla pagina [myaccount.google.com/permissions](https://myaccount.google.com/permissions) oppure scrivendo a privacy@housetag.it.

## 7-bis. Condivisione, trasferimento e divulgazione dei dati (Google OAuth)
I dati ottenuti tramite Google OAuth (inclusi nome, email, foto profilo e accesso a Google Calendar) NON vengono condivisi, venduti, trasferiti o divulgati a terze parti, ad eccezione dei seguenti fornitori tecnici strettamente necessari al funzionamento della piattaforma, vincolati da accordi di riservatezza: servizi di hosting e database (solo per archiviazione sicura). I dati Google NON vengono utilizzati per pubblicità mirata, profilazione commerciale, vendita a terzi o addestramento di modelli di intelligenza artificiale generici. L'unico utilizzo consentito è l'autenticazione dell'utente e la sincronizzazione degli appuntamenti con Google Calendar all'interno della piattaforma Housetag.

## 8. Protezione dei dati sensibili
HouseTag adotta misure tecniche e organizzative adeguate a garantire un livello di sicurezza appropriato al rischio, in conformità all'art. 32 GDPR:

- **Cifratura in transito:** tutte le comunicazioni tra il tuo dispositivo e i nostri server avvengono esclusivamente tramite connessioni cifrate **HTTPS/TLS 1.2+**. I dati non transitano mai in chiaro sulla rete pubblica.
- **Cifratura a riposo:** i dati sono conservati su infrastruttura cloud europea con **cifratura at-rest** a livello di disco e di database.
- **Controllo accessi:** l'accesso ai dati personali è **limitato al solo personale autorizzato** di HouseTag, secondo il principio del privilegio minimo (RBAC — Role-Based Access Control). Ogni operatore accede unicamente ai dati strettamente necessari alle proprie funzioni.
- **Autenticazione e audit:** account protetti da password robuste, opzionale autenticazione a due fattori (2FA), passkey WebAuthn e log di audit immutabili di tutte le operazioni sensibili (accessi, modifiche, esportazioni).
- **Data-Protection by Design & by Default:** ogni nuova funzionalità è progettata seguendo i principi di minimizzazione, integrità e riservatezza dei dati.
- **Gestione degli incidenti:** in caso di violazione dei dati personali (data breach), HouseTag notifica il Garante Privacy entro 72 ore e, ove necessario, informa tempestivamente gli interessati come previsto dagli artt. 33-34 GDPR.

## 8-bis. Protezione dei dati sensibili (Google OAuth)
I dati ottenuti tramite gli scope Google (Calendar, profilo account) sono protetti dalle seguenti misure di sicurezza: (1) Crittografia in transito: tutte le comunicazioni avvengono esclusivamente tramite HTTPS/TLS. (2) Crittografia a riposo: i dati sensibili sono archiviati in forma cifrata. (3) Controllo degli accessi: solo il personale tecnico autorizzato può accedere ai dati, esclusivamente per manutenzione della piattaforma. (4) Conservazione limitata: i dati Google vengono conservati solo per la durata dell'account attivo. (5) Cancellazione: l'utente può revocare l'accesso Google da Google Account > Sicurezza > App con accesso e richiedere la cancellazione completa dei propri dati da Impostazioni > Privacy su Housetag. Dopo la cancellazione i dati vengono rimossi entro 30 giorni.

## 9. Trasferimento extra-UE
Non è previsto alcun trasferimento extra-UE dei dati personali.

## 10. Diritti dell'interessato
Accesso, rettifica, cancellazione, limitazione, portabilità, opposizione, revoca del consenso. Reclamo al Garante Privacy. Scrivere a privacy@housetag.it per esercitare i tuoi diritti.

## Termini di utilizzo

### 11. Accettazione
L'accesso alla piattaforma è riservato a professionisti approvati dal back office. La registrazione richiede l'accettazione integrale dei presenti termini.

### 12. Uso della piattaforma
L'utente si impegna a utilizzare Housetag esclusivamente per finalità professionali connesse alla propria attività immobiliare, garantendo la veridicità dei dati inseriti e la titolarità dei documenti caricati.

### 13. Marketplace e plafond
Il professionista ha accesso a un plafond mensile configurato dall'Amministratore. Al superamento dell'80% viene inviata comunicazione di alert. Il saldo finale è dovuto a fine mese secondo le condizioni economiche della propria fascia commerciale.

### 14. Responsabilità
Housetag agisce come intermediario tra il professionista e i propri clienti finali. La qualità dei servizi tecnici erogati è garantita dagli operatori qualificati assegnati dalla piattaforma.

### 15. Recesso e cessazione
L'utente può richiedere la cancellazione dell'account in ogni momento scrivendo a supporto@housetag.it. Housetag si riserva la facoltà di sospendere account non conformi.

### 16. Foro competente
Per ogni controversia è competente il Foro di Milano, salvo diverse disposizioni imperative di legge.
`;

export default function PrivacyPolicy() {
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/public/policy?slug=privacy")
      .then(r => setPolicy(r.data && !r.data.empty ? r.data : null))
      .catch(() => setPolicy(null))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-purple-700 hover:underline mb-6" data-testid="privacy-back">
          <ArrowLeft size={16} /> Torna alla home
        </Link>
        <div className="label-eyebrow">Legale</div>
        <h1 className="font-display text-3xl md:text-5xl font-bold tracking-tight mt-2 mb-2">
          {policy?.title || "Privacy Policy"}
        </h1>
        {policy?.version && (
          <p className="text-sm text-muted-foreground">
            Versione {policy.version}
            {policy.updated_at && ` — ultimo aggiornamento ${new Date(policy.updated_at).toLocaleDateString("it-IT")}`}
          </p>
        )}

        {loading ? (
          <div className="mt-8 space-y-3">
            <div className="h-4 bg-slate-100 rounded w-3/4 animate-pulse" />
            <div className="h-4 bg-slate-100 rounded w-2/3 animate-pulse" />
            <div className="h-4 bg-slate-100 rounded w-4/5 animate-pulse" />
          </div>
        ) : policy?.body ? (
          <article className="prose prose-slate prose-sm md:prose-base max-w-none mt-6" data-testid="privacy-content">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                h1: ({ children }) => <h2 className="font-display text-xl md:text-2xl font-bold tracking-tight mt-8 mb-3">{children}</h2>,
                h2: ({ children }) => <h2 className="font-display text-xl md:text-2xl font-bold tracking-tight mt-8 mb-3">{children}</h2>,
                h3: ({ children }) => <h3 className="font-semibold text-base md:text-lg mt-5 mb-2">{children}</h3>,
                p: ({ children }) => <p className="text-sm md:text-base text-slate-700 leading-relaxed mb-3">{children}</p>,
                ul: ({ children }) => <ul className="list-disc list-inside text-sm md:text-base text-slate-700 space-y-1 mb-3">{children}</ul>,
                ol: ({ children }) => <ol className="list-decimal list-inside text-sm md:text-base text-slate-700 space-y-1 mb-3">{children}</ol>,
                a: ({ href = "", children }) => href.startsWith("/") ? (
                  <Link to={href} className="text-purple-700 underline">{children}</Link>
                ) : (
                  <a href={href} target="_blank" rel="noreferrer" className="text-purple-700 underline">{children}</a>
                ),
                strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                code: ({ children }) => <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">{children}</code>,
              }}
            >
              {policy.body}
            </ReactMarkdown>
          </article>
        ) : (
          <article className="prose prose-slate prose-sm md:prose-base max-w-none mt-6" data-testid="privacy-content-static">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                h1: ({ children }) => <h2 className="font-display text-xl md:text-2xl font-bold tracking-tight mt-8 mb-3">{children}</h2>,
                h2: ({ children }) => <h2 className="font-display text-xl md:text-2xl font-bold tracking-tight mt-8 mb-3">{children}</h2>,
                h3: ({ children }) => <h3 className="font-semibold text-base md:text-lg mt-5 mb-2">{children}</h3>,
                p: ({ children }) => <p className="text-sm md:text-base text-slate-700 leading-relaxed mb-3">{children}</p>,
                ul: ({ children }) => <ul className="list-disc list-inside text-sm md:text-base text-slate-700 space-y-1 mb-3">{children}</ul>,
                ol: ({ children }) => <ol className="list-decimal list-inside text-sm md:text-base text-slate-700 space-y-1 mb-3">{children}</ol>,
                a: ({ href = "", children }) => href.startsWith("/") ? (
                  <Link to={href} className="text-purple-700 underline">{children}</Link>
                ) : (
                  <a href={href} target="_blank" rel="noreferrer" className="text-purple-700 underline">{children}</a>
                ),
                strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                code: ({ children }) => <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">{children}</code>,
              }}
            >
              {STATIC_FALLBACK_BODY}
            </ReactMarkdown>
          </article>
        )}

        <div className="mt-10 pt-6 border-t text-sm text-muted-foreground">
          Vedi anche i nostri{" "}
          <Link to="/terms" className="text-purple-700 underline" data-testid="privacy-to-terms">
            Termini di Servizio
          </Link>.
        </div>
      </div>
    </div>
  );
}
